/* eslint-disable complexity */
import { IBeeDataContractDoc } from 'bg-server/src/components/api/v1/beedata/types';
import { IExpedient } from 'bg-server/src/components/api/v1/expedient/types';
import { ITramitEmailTemplate } from 'bg-server/src/components/api/v1/form/types';
import { ITramit, ITramitPopulated, ITramitTramitacioData } from 'bg-server/src/components/api/v1/tramit/types';

import { ENV } from 'src/config/environments';
import { httpRequest } from 'src/helpers/http';
import { ITramitSearchFilter } from 'src/interfaces';

class TramitService {
    private getApiEndpoint(): string {
        const selectedCity = localStorage.getItem('selectedCity') || ENV.apiEndpoints[0].key;
        const apiEndpoint = ENV.apiEndpoints.find(endpoint => endpoint.key === selectedCity);
        return apiEndpoint ? `${apiEndpoint.url}/tramit` : `${ENV.apiEndpoints[0].url}/tramit`;
    }

    public doesTramitPassFilter(tramit: ITramitPopulated, filter: ITramitSearchFilter): boolean {
        // Check estat
        if (filter.estat && tramit.estat !== filter.estat) {
            return false;
        }

        // Check districts
        if (filter.districts && filter.districts.length > 0) {
            if (!filter.districts.includes(tramit.districte)) {
                return false;
            }
        }

        // Check ajuntamentRequired
        if (filter.isAjuntament && !tramit.ajuntamentRequired) {
            return false;
        }

        // Check prioritat
        if (filter.prioritat && filter.prioritat.length > 0) {
            if (!filter.prioritat.includes(tramit.prioritat)) {
                return false;
            }
        }

        // Check professionals
        if (filter.professionals && filter.professionals.length > 0) {
            const professionalId = typeof tramit.professional === 'string'
                ? tramit.professional
                : tramit.professional?._id;
            if (!filter.professionals.includes(professionalId)) {
                return false;
            }
        }

        // Check professionalsAj
        if (filter.professionalsAj && filter.professionalsAj.length > 0) {
            const ajuntamentUserId = typeof tramit.ajuntamentRelatedUser === 'string'
                ? tramit.ajuntamentRelatedUser
                : tramit.ajuntamentRelatedUser?._id;

            if (filter.professionalsAj.includes(null)) {
                // If null is in the filter, accept tramits without ajuntamentRelatedUser
                if (ajuntamentUserId !== null && ajuntamentUserId !== undefined) {
                    return false;
                }
            } else if (!filter.professionalsAj.includes(ajuntamentUserId)) {
                return false;
            }
        }

        // Check isOnWaitingAjuntament
        if (filter.isOnWaitingAjuntament && filter.isOnWaitingAjuntament.length > 0) {
            const booleanEstats = filter.isOnWaitingAjuntament.map(estat => parseInt(estat, 10) === 1);
            const isWaiting = tramit.isAjuntamentWaiting === true;
            const isNotWaiting = !tramit.isAjuntamentWaiting;

            const matches = booleanEstats.some(estat => (estat === true && isWaiting) || (estat === false && isNotWaiting));

            if (!matches) {
                return false;
            }
        }

        // Check formType
        if (filter.formType && filter.formType.length > 0) {
            if (!filter.formType.includes(tramit.formType)) {
                return false;
            }
        }

        // Check date range
        if (filter.startDate || filter.endDate) {
            const tramitDate = new Date(tramit.created);

            if (filter.startDate) {
                const startDate = new Date(filter.startDate);
                if (tramitDate < startDate) {
                    return false;
                }
            }

            if (filter.endDate) {
                const endDate = new Date(filter.endDate);
                if (tramitDate > endDate) {
                    return false;
                }
            }
        }

        return true;
    }

    public tranformSearchFilterTramit(filter: ITramitSearchFilter) {
        const searchField: any = {
            $and: [],
        };

        if (filter.estat) {
            searchField.$and.push({ estat: { $in: [filter.estat] } });
        }

        if (filter.districts && filter.districts.length > 0) {
            searchField.$and.push({
                districte: {
                    $in: filter.districts,
                },
            });
        }

        if (filter.isAjuntament) {
            searchField.$and.push({ ajuntamentRequired: true });
        }

        if (filter.prioritat) {
            searchField.$and.push({ prioritat: { $in: filter.prioritat } });
        }

        if (filter.professionals && filter.professionals.length > 0) {
            searchField.$and.push({ professional: { $in: filter.professionals } });
        }

        if (filter.professionalsAj && filter.professionalsAj.length > 0) {
            if (filter.professionalsAj.includes(null)) {
                searchField.$and.push({
                    $or: [
                        { ajuntamentRelatedUser: { $exists: false } },
                        { ajuntamentRelatedUser: { $eq: null } },
                    ],
                });
            } else {
                searchField.$and.push({ ajuntamentRelatedUser: { $in: filter.professionalsAj } });
            }
        }

        if (filter.isOnWaitingAjuntament && filter.isOnWaitingAjuntament.length > 0) {
            const booleanEstats = filter.isOnWaitingAjuntament.map(estat => parseInt(estat, 10) === 1);
            const orConditions = [];

            booleanEstats.forEach((estat) => {
                if (estat === true) {
                    orConditions.push({ isAjuntamentWaiting: true });
                } else {
                    orConditions.push(
                        { isAjuntamentWaiting: false },
                        { isAjuntamentWaiting: { $exists: false } },
                        { isAjuntamentWaiting: null },
                    );
                }
            });

            searchField.$and.push({ $or: orConditions });
        }

        if (filter.formType) {
            searchField.$and.push({ formType: { $in: filter.formType } });
        }

        if (filter.startDate && filter.endDate) {
            searchField.$and.push(
                { created: { $gte: filter.startDate } },
                { created: { $lte: filter.endDate } },
            );
        } else if (filter.startDate) {
            searchField.$and.push({ created: { $gte: filter.startDate } });
        } else if (filter.endDate) {
            searchField.$and.push({ created: { $lte: filter.endDate } });
        }

        return searchField;
    }

    public async searchTramitsByFilter(filter: ITramitSearchFilter, page: number, pageSize = 20): Promise<{ count: number; data: ITramitPopulated[] }> {
        const sFilter = this.tranformSearchFilterTramit(filter);
        const hasSearchFilter = (
            filter.startDate
            || filter.endDate
            || (filter.prioritat && filter.prioritat.length > 0)
            || (filter.formType && filter.formType.length > 0)
            || (filter.districts && filter.districts.length > 0)
            || filter.estat
            || (filter.isOnWaitingAjuntament && filter.isOnWaitingAjuntament.length > 0)
            || (filter.professionals && filter.professionals.length > 0)
            || (filter.professionalsAj && filter.professionalsAj.length > 0)
        );

        const filterParams = hasSearchFilter
            ? `search=${encodeURIComponent(JSON.stringify(sFilter))}&`
            : '';
        let orderBy = '';

        if (filter.dateOrderBy || filter.prioritatOrderBy) {
            orderBy = `sort=${filter.prioritatOrderBy ? filter.prioritatOrderBy : ''} ${filter.dateOrderBy ? filter.dateOrderBy : ''} &`;
        }
        const pageParams = page ? `page=${page}&` : '';
        const pageSizeParams = pageSize ? `pageitems=${pageSize}` : '';

        const response = await httpRequest(`${this.getApiEndpoint()}?${orderBy}${filterParams}${pageParams}${pageSizeParams}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async addTramitDocType(tramitId: string, docNumber: string): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const body = {
            docNumber,
        };
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/document`, 'POST', body);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async getTramitById(tramitId: string): Promise<ITramit> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async getTramitPopulatedById(tramitId: string): Promise<ITramitPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/populated`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async createTramit(data:any): Promise<ITramitPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/`, 'POST', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async updateTramit(tramitId: string, data: any): Promise<ITramitPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}`, 'PUT', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async confirmTramit(tramitId: string, data: any): Promise<ITramitPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/confirm`, 'POST', data);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async sendNotification(tramitId: string, notType: string): Promise<ITramitPopulated> {
        const body = {
            notType,
        };
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/notify`, 'POST', body);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async cancelTramit(tramitId: string, motiuCancelacio: string): Promise<ITramitPopulated> {
        const body = {
            motiuCancelacio,
        };
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/cancel`, 'POST', body);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async sendTramitacioEmail(tramitId: string, body: ITramitTramitacioData): Promise<{ tramit: ITramitPopulated, expedient: IExpedient }> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/tramitacio`, 'POST', body);
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async getTramitacioEmails(tramitId: string): Promise<ITramitEmailTemplate[]> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/tramitacio`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async getBeeDataContract(tramitId: string): Promise<IBeeDataContractDoc> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/beedata-contract`, 'GET');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async assignAjuntamentUser(tramitId: string): Promise<ITramitPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/assign-ajuntament-user`, 'PUT');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }

    public async toggleAjuntamentPending(tramitId: string): Promise<ITramitPopulated> {
        const response = await httpRequest(`${this.getApiEndpoint()}/${tramitId}/toggle-ajuntament-pending`, 'PUT');
        const json = await response.json();
        if (json.error !== undefined) {
            await Promise.reject(json.message);
        }
        return json;
    }
}

export const tramitService = new TramitService();
